import type { BaseRecord, DataProvider } from "@refinedev/core";

import { api } from "../api/client";

/**
 * Refine data provider for `/admin/<resource>` (04 §7):
 * lists use `?q&sort&page&per_page`, envelope `{data, meta:{page,per_page,total}}`.
 * Resources land in Phase 1; the provider is wired now so screens only need
 * `useList`/`useOne`/`useForm`.
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => "/api/v1/admin",

  getList: async <TData extends BaseRecord = BaseRecord>({
    resource,
    pagination,
    sorters,
    filters,
  }: Parameters<DataProvider["getList"]>[0]) => {
    const page = pagination?.currentPage ?? 1;
    const per_page = pagination?.pageSize ?? 20;
    const sort = sorters?.[0] ? `${sorters[0].order === "desc" ? "-" : ""}${sorters[0].field}` : undefined;
    const q = filters?.find((f) => "field" in f && f.field === "q" && typeof f.value === "string")?.value as
      | string
      | undefined;
    const { data, meta } = await api<TData[]>(`/admin/${resource}`, { query: { page, per_page, sort, q } });
    return { data, total: meta?.total ?? data.length };
  },

  getOne: async <TData extends BaseRecord = BaseRecord>({ resource, id }: Parameters<DataProvider["getOne"]>[0]) => {
    const { data } = await api<TData>(`/admin/${resource}/${id}`);
    return { data };
  },

  create: async <TData extends BaseRecord = BaseRecord, TVariables = object>({
    resource,
    variables,
  }: Parameters<NonNullable<DataProvider["create"]>>[0] & { variables: TVariables }) => {
    const { data } = await api<TData>(`/admin/${resource}`, { method: "POST", body: variables });
    return { data };
  },

  update: async <TData extends BaseRecord = BaseRecord, TVariables = object>({
    resource,
    id,
    variables,
  }: Parameters<NonNullable<DataProvider["update"]>>[0] & { variables: TVariables }) => {
    const { data } = await api<TData>(`/admin/${resource}/${id}`, { method: "PATCH", body: variables });
    return { data };
  },

  deleteOne: async <TData extends BaseRecord = BaseRecord>({
    resource,
    id,
  }: Parameters<NonNullable<DataProvider["deleteOne"]>>[0]) => {
    await api(`/admin/${resource}/${id}`, { method: "DELETE" });
    return { data: { id } as unknown as TData };
  },

  custom: async <TData extends BaseRecord = BaseRecord>({
    url,
    method,
    payload,
    query,
  }: Parameters<NonNullable<DataProvider["custom"]>>[0]) => {
    const { data } = await api<TData>(url.replace(/^\/api\/v1/, ""), {
      method: (method?.toUpperCase() as "GET" | "POST" | "PUT" | "PATCH" | "DELETE") ?? "GET",
      body: payload,
      query: query as Record<string, string> | undefined,
    });
    return { data };
  },
};
